import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SequencerProfileData {
  // Profile information
  id: string;
  userId: string;
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  bio: string | null;
  socialLinks: Record<string, string> | null;
  location: string | null;
  websiteUrl: string | null;
  featured: boolean;
  verified: boolean;
  createdAt: string;

  // Aggregate statistics
  totalSequences: number;
  freeSequenceCount: number;
  paidSequenceCount: number;
  totalDownloads: number;
  averageRating: number;
  reviewCount: number;

  // Sequences by this creator
  sequences: any[];
}

interface UseSequencerProfileOptions {
  username?: string;
  userId?: string;
}

interface UseSequencerProfileResult {
  profile: SequencerProfileData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch a single sequencer's profile with full details and their sequences.
 *
 * Fetches:
 * - Complete user profile (bio, social links, badges, etc.)
 * - All sequences created by this user
 * - Aggregate statistics (sequence counts, downloads, average rating)
 *
 * Can fetch by username OR user_id. Username is preferred for SEO-friendly URLs.
 *
 * @param options - Either username or userId to identify the creator
 * @returns SequencerProfileData with loading and error states
 */
export function useSequencerProfile(
  options: UseSequencerProfileOptions
): UseSequencerProfileResult {
  const { username, userId } = options;
  const [profile, setProfile] = useState<SequencerProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Must provide either username or userId
    if (!username && !userId) {
      setError('Username or userId is required');
      setIsLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch user profile
        let profileQuery = supabase
          .from('user_profiles')
          .select('*')
          .limit(1);

        if (username) {
          profileQuery = profileQuery.eq('username', username);
        } else if (userId) {
          profileQuery = profileQuery.eq('user_id', userId);
        }

        const { data: profileData, error: profileError } = await profileQuery.single();

        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          throw new Error('Creator not found');
        }

        if (!profileData) {
          throw new Error('Creator not found');
        }

        // Fetch all sequences by this creator
        const { data: sequences, error: sequencesError } = await supabase
          .from('sequences')
          .select('*')
          .eq('user_id', profileData.user_id)
          .order('created_at', { ascending: false });

        if (sequencesError) {
          console.error('Error fetching sequences:', sequencesError);
          throw new Error(sequencesError.message);
        }

        const creatorSequences = sequences || [];

        // Calculate statistics
        let totalSequences = creatorSequences.length;
        let freeSequenceCount = 0;
        let paidSequenceCount = 0;
        let totalDownloads = 0;

        creatorSequences.forEach((seq) => {
          totalDownloads += seq.download_count || 0;

          const price = seq.price ? parseFloat(seq.price.toString()) : 0;
          if (price === 0) {
            freeSequenceCount++;
          } else {
            paidSequenceCount++;
          }
        });

        // Fetch reviews for this creator's sequences
        const sequenceIds = creatorSequences.map((s) => s.id);
        let averageRating = 0;
        let reviewCount = 0;

        if (sequenceIds.length > 0) {
          const { data: reviews, error: reviewsError } = await supabase
            .from('reviews')
            .select('rating')
            .eq('target_type', 'sequence')
            .in('target_id', sequenceIds);

          if (reviewsError) {
            console.error('Error fetching reviews:', reviewsError);
            // Don't throw - just continue without ratings
          } else if (reviews && reviews.length > 0) {
            reviewCount = reviews.length;
            const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
            averageRating = Math.round((totalRating / reviewCount) * 10) / 10; // Round to 1 decimal
          }
        }

        // Combine all data
        const profileResult: SequencerProfileData = {
          id: profileData.id,
          userId: profileData.user_id,
          displayName: profileData.display_name,
          username: profileData.username,
          avatarUrl: profileData.avatar_url,
          bio: profileData.bio,
          socialLinks: profileData.social_links as Record<string, string> | null,
          location: profileData.location,
          websiteUrl: profileData.website_url,
          featured: profileData.featured || false,
          verified: profileData.verified || false,
          createdAt: profileData.created_at,
          totalSequences,
          freeSequenceCount,
          paidSequenceCount,
          totalDownloads,
          averageRating,
          reviewCount,
          sequences: creatorSequences,
        };

        setProfile(profileResult);
        console.log(`Loaded profile for ${profileData.username || profileData.user_id}:`, {
          totalSequences,
          totalDownloads,
          averageRating,
        });
      } catch (err) {
        console.error('Error loading sequencer profile:', err);
        setError(err instanceof Error ? err.message : 'Failed to load creator profile');
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [username, userId]);

  return {
    profile,
    isLoading,
    error,
  };
}
